import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Recognition {
  type: 'certification' | 'partnership' | 'media';
  title: string;
  organization: string;
  logo: string;
  alt: string;
  date: string;
  description: string;
}

interface IndustryRecognitionProps {
  recognitions: Recognition[];
}

const IndustryRecognition = ({ recognitions }: IndustryRecognitionProps) => {
  const getIcon = (type: string) => {
    switch (type) {
      case 'certification':
        return 'ShieldCheckIcon';
      case 'partnership':
        return 'HandshakeIcon';
      case 'media':
        return 'NewspaperIcon';
      default:
        return 'StarIcon';
    }
  };

  const getColor = (type: string) => {
    switch (type) {
      case 'certification':
        return 'text-success';
      case 'partnership':
        return 'text-primary';
      case 'media':
        return 'text-accent';
      default:
        return 'text-foreground';
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-4">
          Erkend door toonaangevende organisaties
        </h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Onze expertise en resultaten worden erkend door vooraanstaande certificeringsinstanties, technologiepartners en media.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {recognitions.map((recognition, index) => (
          <div
            key={index}
            className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-all duration-500 hover:shadow-premium group"
          >
            <div className="flex items-start space-x-4 mb-4">
              <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${recognition.type === 'certification' ? 'from-success/20 to-success/5' : recognition.type === 'partnership' ? 'from-primary/20 to-primary/5' : 'from-accent/20 to-accent/5'} flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                <Icon name={getIcon(recognition.type) as any} size={24} className={getColor(recognition.type)} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${getColor(recognition.type)}`}>
                    {recognition.type === 'certification' ? 'Certificering' : recognition.type === 'partnership' ? 'Partnerschap' : 'Media'}
                  </span>
                  <span className="text-xs text-muted-foreground">{recognition.date}</span>
                </div>
                <h3 className="text-lg font-display font-bold text-foreground mb-1 group-hover:text-primary transition-colors duration-300">
                  {recognition.title}
                </h3>
                <p className="text-sm font-medium text-primary mb-3">
                  {recognition.organization}
                </p>
              </div>
            </div>

            <div className="relative h-20 mb-4 rounded-lg overflow-hidden bg-muted/30 flex items-center justify-center">
              <AppImage
                src={recognition.logo}
                alt={recognition.alt}
                className="max-h-full max-w-full object-contain p-4"
              />
            </div>

            <p className="text-sm text-foreground/80 leading-relaxed">
              {recognition.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IndustryRecognition;